import { queryOptions, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { ApiClient } from '../api/client';
import { useApi } from './provider';
import { workspaceKeys } from './workspaces';

export const invitationKeys = {
  pending: (workspaceSlug: string) => ['invitations', 'pending', workspaceSlug] as const,
  preview: (token: string) => ['invitations', 'preview', token] as const,
};

export interface InviteMemberInput {
  email: string;
  role: 'admin' | 'member';
}

/**
 * Pending invitations for a workspace. Admin/owner only — non-admins
 * 403 from the API and the SPA gates the People page's invitation
 * section behind `useMyWorkspaceMembership(slug).isAdmin`.
 */
export const pendingInvitationsQueryOptions = (api: ApiClient, workspaceSlug: string) =>
  queryOptions({
    queryKey: invitationKeys.pending(workspaceSlug),
    queryFn: async () => {
      const { data, error } = await api.GET('/v1/workspaces/{workspace_slug}/invitations', {
        params: { path: { workspace_slug: workspaceSlug } },
      });
      if (error) throw error;
      return data;
    },
  });

export const usePendingInvitations = (workspaceSlug: string | undefined) => {
  const api = useApi();
  return useQuery({
    ...pendingInvitationsQueryOptions(api, workspaceSlug ?? ''),
    enabled: Boolean(workspaceSlug),
  });
};

export const useInviteMember = (workspaceSlug: string) => {
  const api = useApi();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: InviteMemberInput) => {
      const { data, error } = await api.POST('/v1/workspaces/{workspace_slug}/members', {
        params: { path: { workspace_slug: workspaceSlug } },
        body: input,
      });
      if (error) throw error;
      return data;
    },
    onSuccess: async () => {
      await qc.invalidateQueries({ queryKey: invitationKeys.pending(workspaceSlug) });
    },
  });
};

export const useRevokeInvitation = (workspaceSlug: string) => {
  const api = useApi();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      const { error } = await api.DELETE(
        '/v1/workspaces/{workspace_slug}/invitations/{id}',
        {
          params: { path: { workspace_slug: workspaceSlug, id } },
        },
      );
      if (error) throw error;
    },
    onSuccess: async () => {
      await qc.invalidateQueries({ queryKey: invitationKeys.pending(workspaceSlug) });
    },
  });
};

/**
 * Public (unauthenticated) invitation preview — used by the
 * `/i/{token}` accept page to render workspace name + role + email
 * before prompting for sign-in. `retry: false` keeps the 410
 * (expired/accepted) from looping.
 */
export const invitationPreviewQueryOptions = (api: ApiClient, token: string) =>
  queryOptions({
    queryKey: invitationKeys.preview(token),
    retry: false,
    queryFn: async () => {
      const { data, error } = await api.GET('/v1/invitations/{token}', {
        params: { path: { token } },
      });
      if (error) throw error;
      return data;
    },
  });

export const useInvitationPreview = (token: string | undefined) => {
  const api = useApi();
  return useQuery({
    ...invitationPreviewQueryOptions(api, token ?? ''),
    enabled: Boolean(token),
  });
};

export const useAcceptInvitation = () => {
  const api = useApi();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (token: string) => {
      const { data, error } = await api.POST('/v1/invitations/{token}/accept', {
        params: { path: { token } },
      });
      if (error) throw error;
      return data;
    },
    onSuccess: async () => {
      // The user just joined a workspace — refresh the workspaces
      // list so the sidebar switcher picks it up.
      await qc.invalidateQueries({ queryKey: workspaceKeys.all });
    },
  });
};

// ── Member-management mutations (live alongside the workspace-members
// list query that already exists in `queries/workspaces.ts`).

export interface UpdateMemberRoleInput {
  userId: string;
  role: 'admin' | 'member';
}

export const useUpdateMemberRole = (workspaceSlug: string) => {
  const api = useApi();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: UpdateMemberRoleInput) => {
      const { userId, role } = input;
      const { data, error } = await api.PATCH(
        '/v1/workspaces/{workspace_slug}/members/{userId}',
        {
          params: { path: { workspace_slug: workspaceSlug, userId } },
          body: { role },
        },
      );
      if (error) throw error;
      return data;
    },
    onSuccess: async () => {
      await qc.invalidateQueries({ queryKey: workspaceKeys.members(workspaceSlug) });
    },
  });
};

export const useRemoveMember = (workspaceSlug: string) => {
  const api = useApi();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (userId: string) => {
      const { error } = await api.DELETE(
        '/v1/workspaces/{workspace_slug}/members/{userId}',
        {
          params: { path: { workspace_slug: workspaceSlug, userId } },
        },
      );
      if (error) throw error;
    },
    onSuccess: async () => {
      await qc.invalidateQueries({ queryKey: workspaceKeys.members(workspaceSlug) });
      // If the user removed themselves, the workspaces list also
      // shrinks; cheap to invalidate.
      await qc.invalidateQueries({ queryKey: workspaceKeys.all });
    },
  });
};
